import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output
} from '@angular/core';
import { ControlValueAccessor, FormControl, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { noop } from 'rxjs/internal/util/noop';
import { FormElementFieldValidationItem } from '../../types/form-element-field-validation-item.type';

/**
 * A generic abstract class which provides the base properties and functionality for generic form components.
 */
@Component({ template: '', standalone: true, changeDetection: ChangeDetectionStrategy.OnPush })
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export abstract class DeightonGenericFormFieldComponent<TComponent, TModelValue>
  implements ControlValueAccessor, AfterViewInit
{
  /**
   * Deighton Generic Form Field class constructor.
   */
  constructor() {}

  /**
   * ngAfterViewInit Angular lifecycle hook.
   *
   * A callback method that is invoked immediately after
   * Angular has completed initialization of a component's view.
   * It is invoked only once when the view is instantiated.
   */
  public ngAfterViewInit(): void {
    // Ensure the modelValue variable is initialized with the form control value
    if (this.formControl.value !== null && this._modelValue === null) {
      this._modelValue = this.formControl.value;
    }
    this.setRequiredValidation();
  }

  /**
   * Add or remove required validation to the form field based on isRequired. This is in a separate method so it can be overridden in extended form field types (ie. autocomplete, where the conditions are different)
   */
  public setRequiredValidation(): void {
    if (this.isRequired === true) {
      this.addValidator('required', Validators.required);
    } else {
      this.removeValidator('required');
    }
  }

  /**
   * Internal data model value.
   */
  protected _modelValue: TModelValue = null;

  /**
   * Get accessor for ngModel.
   */
  public get modelValue(): TModelValue {
    return this._modelValue;
  }

  /**
   * Set accessor for ngModel.
   */
  @Input()
  public set modelValue(value: TModelValue) {
    this._modelValue = value;
    this.formControl.setValue(value);
    this.onChangeCallback(value);
    this.modelValueChange.emit(value);
  }

  /**
   * Event emitter for handling the `modelValueChange` event.
   */
  @Output() public modelValueChange: EventEmitter<TModelValue> = new EventEmitter<TModelValue>();

  /**
   * The id values applies to the container DOM element of this component.
   */
  @Input() public id: string = null;

  public get disabled(): boolean {
    return this.formControl.disabled;
  }

  /**
   * Controls the disabled / readonly status of the component.
   */
  @Input() public set disabled(value: boolean) {
    if (value) {
      this.formControl.disable();
    } else {
      this.formControl.enable();
    }
  }

  /**
   * Determines is a value is required for this component to pass validation.
   */
  private _isRequired: boolean = false;
  /**
   * Determines is a value is required for this component to pass validation. Getter
   */
  public get isRequired(): boolean {
    return this._isRequired;
  }
  /**
   * Determines is a value is required for this component to pass validation. Setter
   */
  @Input() public set isRequired(value: boolean) {
    this._isRequired = value;
    if (this.formControl) {
      this.setRequiredValidation();
    }
  }

  /**
   * Color property.
   */
  @Input() public color: 'primary' | 'accent' | 'warn' = 'primary';

  /**
   * Label text for the control.
   */
  @Input() public label: string = null;

  /**
   *  Tooltip to be displayed for the text control. The tooltip will only be displayed is a value is set.
   */
  @Input() public tooltip: string = null;

  /**
   * Placeholder text to be displayed when no value has been given to this component.
   */
  @Input()
  public placeholder: string = null;

  /**
   * The default state of the components label. By setting the state to 'auto', the label will only
   * be set to a floating state after the user has focused or entered a value for the component.
   */
  @Input()
  public floatLabel: 'auto' | 'always' = 'auto';

  /**
   * Controls the hint text displayed for this component.
   */
  @Input()
  public hintText: string = null;

  //   /* Property this component is referencing */
  //   @Input() public forProperty: string = null;

  // /* Event emitter for model change event */
  // @Output() public modelChange: EventEmitter<TModelValue> = new EventEmitter<TModelValue>();

  //   /* Event to fire after validation has completed */
  //   @Output() public validationComplete: EventEmitter<any> = new EventEmitter<any>();

  //   /* Model control child component */
  //   @ViewChild('modelComponent') public modelComponent: any;

  protected _formControl: FormControl<TModelValue> = new FormControl<TModelValue>(null, []);
  public get formControl(): FormControl<TModelValue> {
    return this._formControl;
  }
  /**
   * Tracks the value and validation status of an individual form control.
   *
   * This is one of the four fundamental building blocks of Angular forms, along with
   * `FormGroup`, `FormArray` and `FormRecord`. It extends the `AbstractControl` class that
   * implements most of the base functionality for accessing the value, validation status,
   * user interactions and events.
   *
   * `FormControl` takes a single generic argument, which describes the type of its value. This
   * argument always implicitly includes `null` because the control can be reset. To change this
   * behavior, set `nonNullable` or see the usage notes below.
   */
  @Input() public set formControl(value: FormControl<TModelValue>) {
    this._formControl = value;
  }

  //   /* Error state matcher for the form control */
  //   public errorMatcher: DeightonErrorStateMatcher = new DeightonErrorStateMatcher();

  /**
   *  Validators to be used in the control.
   */
  private validators: Record<string, ValidatorFn> = {};

  // /* Async Validators to be used in the control */
  // private asyncValidators: Record<string, ValidatorFn> = {};

  /**
   * Stores the custom validation expressions that have been added to this field
   */
  protected validationExpressionItems: Array<FormElementFieldValidationItem> = [];

  /**
   * Getter for current validation error message
   */
  public get customValidationErrorMessage(): string {
    let firstErrorMessage: string = null;
    if (this.formControl.invalid) {
      // List is stored here sorted so the first one is the correct one to pull
      for (const valExprItem of this.validationExpressionItems) {
        if (valExprItem.id in this.formControl.errors) {
          firstErrorMessage = valExprItem.errorMessage;
          break;
        }
      }
    }
    return firstErrorMessage;
  }

  //   private _customValidationErrorMessage: string = '';

  //   public get customValidationErrorMessage(): string {
  //     return this._customValidationErrorMessage;
  //   }

  //   /* Custom Validation Error Message for the input */
  //   @Input()
  //   public set customValidationErrorMessage(value: string) {
  //     const valueChanged: boolean = value != this._customValidationErrorMessage;
  //     this._customValidationErrorMessage = value;
  //     if (valueChanged) {
  //       this.formControl.updateValueAndValidity();
  //       if (value) {
  //         this.formControl.markAsTouched();
  //       }
  //     }
  //   }

  //   /**
  //    * Checks if there is a custom validation method passed in, if so will return an error, and display the error message under the input.
  //    */
  //   public customErrorMessageValidation = (): ValidatorFn => {
  //     return (): { [key: string]: boolean } | null => {
  //       if (this.customValidationErrorMessage != null && this.customValidationErrorMessage.length > 0) {
  //         return { customValidationErrorMessage: true };
  //       }
  //       return null;
  //     };
  //   };

  /**
   * Add a validator to the fields validation collection.
   *
   * @param validator Validator type to add.
   * @param validatorFn Validator function to add.
   */
  protected addValidator(validator: string, validatorFn: ValidatorFn): void {
    if (this.validators[validator] == null) {
      this.validators[validator] = validatorFn;
      this.formControl.setValidators(Object.values(this.validators));
      this.formControl.updateValueAndValidity();
    }
  }

  /**
   * Add a custom validator from an expression that needs to be parsed against the model
   * @param validationMethod The method to execute to check if the model is valid
   * @param validationExpressionItem The validation expression item
   */
  public addValidatorFromExpression(
    validationMethod: () => boolean,
    validationExpressionItem: FormElementFieldValidationItem
  ): void {
    if (this.validators[validationExpressionItem.id] == null) {
      this.validationExpressionItems.push(validationExpressionItem);
      const newValidatorFn: ValidatorFn = (): ValidationErrors => {
        const expressionEvalResult: unknown = validationMethod();
        if (expressionEvalResult === true) {
          return null;
        } else {
          return { [validationExpressionItem.id]: true };
        }
      };

      this.validators[validationExpressionItem.id] = newValidatorFn;

      this.formControl.setValidators(Object.values(this.validators));
      this.formControl.updateValueAndValidity();
    }
  }

  //   /**
  //    * Add a validator in the form of an observable function
  //    * @param validator Validator type to add.
  //    * @param validatorFn Validator function to add.
  //    */
  //   public addAsyncValidator(
  //     validator: string,
  //     obsValidatorFn: (pendingChanges: any) => Observable<any>
  //   ): void {
  //     if (this.asyncValidators[validator] == null) {
  //       this.asyncValidators[validator] = this.asyncValidatorFromObservable(obsValidatorFn);
  //       this.formControl.setAsyncValidators(Object.values(this.asyncValidators));
  //     }
  //      this._changeDetectionRef.detectChanges();
  //   }

  //   /**
  //    * Take some observable which notifies with a validation result.
  //    * Create an async validator from this observable.
  //    *
  //    * @param observableFunction some function which will be used for form validation
  //    * @returns some async validator function
  //    */
  //   private asyncValidatorFromObservable(
  //     observableFunction: (pendingChanges: any) => Observable<ValidationResult>
  //   ): AsyncValidatorFn {
  //     return (control: AbstractControl): Observable<{ [key: string]: any } | null | any> => {
  //       return observableFunction(control['_pendingValue']).pipe(
  //         map((response: ValidationResult) => {
  //           const responseObject = {};
  //           responseObject[this.forProperty] = {
  //             value: response.IsValid
  //           };

  //           // wait a frame for the validation message to show up, then emit that the validation flow has finished
  //           waitAFrame().subscribe(() => {
  //             this.validationComplete.emit(responseObject);
  //           });

  //           // if the field is valid, return nothing. If validation failed, return the reason.
  //           if (response.IsValid) {
  //             this.validationErrorMessage = null;
  //             return null;
  //           } else {
  //             this.validationErrorMessage = response.Reason;
  //             return responseObject;
  //           }
  //         })
  //       );
  //     };
  //   }

  /**
   * Remove a validator from the fields validation collection.
   *
   * @param validator Validator type to add.
   * @param validatorFn Validator function to add.
   */
  protected removeValidator(validator: string): void {
    if (this.validators[validator] != null) {
      delete this.validators[validator];
      this.formControl.setValidators(Object.values(this.validators));
      this.formControl.updateValueAndValidity();
    }
  }

  /**
   * Handles the callback function for the `onTouched` event.
   */
  private onTouchedCallback: typeof noop = noop;

  /**
   * Handles the callback function for the `onChange` event.
   */
  // @TODO Cleanup the unknown type here with an actual typing.
  protected onChangeCallback: (_: unknown) => void = noop;

  //   protected internalOnChangeCallback: (_: any) => void = noop;

  /**
   * Handles the ngModel changed event.
   * @param value Value to set the internal ngModel to.
   */
  public writeValue(value: TModelValue): void {
    this._modelValue = value;
  }

  /**
   * Registers the onChange event callback for the ngModel.
   * @param callback Callback function.
   */
  public registerOnChange(callback: typeof noop): void {
    this.onChangeCallback = callback;
  }

  /**
   * Registers the onTouched event callback for the ngModel.
   * @param callback
   */
  public registerOnTouched(callback: typeof noop): void {
    this.onTouchedCallback = callback;
  }

  /**
   * Call the onTouched callback onBlur.
   */
  public onBlur(): void {
    if (this.onTouchedCallback) {
      this.onTouchedCallback();
    }
  }

  //   /**
  //    * Force this component to validate itself if it has a form control assigned to it
  //    */
  //   public forceValidation() {
  //     if (this.formControl != null) {
  //       // Mark the field as touched for validation, update the validity of the field
  //       this.formControl.markAsTouched({ onlySelf: true });
  //       // Execute form validation for this field
  //       this.formControl.updateValueAndValidity({
  //         onlySelf: true,
  //         emitEvent: false
  //       });
  //     }
  //   }
}
